import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LayoutGrid, Sun, Beer, Instagram, LifeBuoy, LogOut, User, ShieldCheck, Clock, LogIn, Trophy } from "lucide-react";
import { api, API, HELP_WHATSAPP_URL, userPhotoUrl, BUTTON_DEFAULTS, STAFF_ROLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BannerCarousel from "@/components/BannerCarousel";
import InstallAppButton from "@/components/InstallAppButton";
import { sortMenu } from "@/components/MenuOrderManager";

const LOGO_URL =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_arena-dubal/artifacts/dgj3odz1_Logo%20Dubal%20branca.png";

const OPTIONS = [
  { key: "reservas", to: "/reservas", icon: LayoutGrid, color: "from-[#1B7A45] to-[#0E4325]" },
  { key: "dayuse", to: "/dayuse", icon: Sun, color: "from-[#E8611A] to-[#B8440E]" },
  { key: "bar", to: "/bar", icon: Beer, color: "from-[#C7962B] to-[#8a6615]" },
  { key: "torneio", to: "/torneio", icon: Trophy, color: "from-[#2563eb] to-[#1e3a8a]" },
  { key: "instagram", href: "https://instagram.com/dubal.esportes", icon: Instagram, color: "from-[#833AB4] via-[#C13584] to-[#F77737]" },
];

function MenuCard({ o, i, bg, hasBg, t }) {
  const inner = (
    <>
      <div className={`absolute inset-0 bg-gradient-to-br ${o.color} ${hasBg ? "opacity-80" : ""}`} />
      <div className="relative">
        <o.icon className="h-9 w-9 text-white/90" />
        <p className="mt-4 font-display text-xl font-black text-white">{t(`${o.key}_title`)}</p>
        <p className="text-sm font-medium text-white/70">{t(`${o.key}_sub`)}</p>
      </div>
    </>
  );
  const cls = "animate-fade-up group relative overflow-hidden rounded-3xl p-6 shadow-xl transition-transform hover:scale-[1.02]";
  const style = { animationDelay: `${i * 80}ms`, ...bg };
  return o.href ? (
    <a href={o.href} target="_blank" rel="noopener noreferrer" data-testid={`menu-${o.key}`} style={style} className={cls}>{inner}</a>
  ) : (
    <Link to={o.to} data-testid={`menu-${o.key}`} style={style} className={cls}>{inner}</Link>
  );
}

export default function MenuPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [logo, setLogo] = useState(LOGO_URL);
  const [mbg, setMbg] = useState({});
  const [texts, setTexts] = useState({});
  const [video, setVideo] = useState(null);
  const [order, setOrder] = useState(null);
  const [tourOn, setTourOn] = useState(false);

  useEffect(() => {
    api.get("/settings").then((res) => {
      if (res.data.logo_image) setLogo(`${API}/settings/logo-image?v=${encodeURIComponent(res.data.logo_image_updated_at)}`);
      setMbg(res.data.menu_bg || {});
      setTexts(res.data.button_texts || {});
      setOrder(sortMenu(res.data.menu_order));
      setTourOn(!!res.data.tournament_enabled);
      if (res.data.menu_video) setVideo(`${API}/settings/menu-video?v=${encodeURIComponent(res.data.menu_video_updated_at)}`);
    }).catch(() => {});
  }, []);

  const t = (k) => texts[k] || BUTTON_DEFAULTS[k];
  const isStaff = user && (STAFF_ROLES.includes(user.role) || user.is_owner) && (user.perms || []).length > 0;
  const pending = user && !user.approved && !isStaff;
  const visibleKeys = (order || []).filter((k) => (k === "torneio" ? user && tourOn : user || k === "reservas" || k === "instagram"));

  const bgStyle = (slug) => {
    const info = mbg[slug];
    if (info?.present) return { backgroundImage: `url(${API}/settings/menu-bg/${slug}?v=${encodeURIComponent(info.updated_at || "")})`, backgroundSize: "cover", backgroundPosition: "center" };
    return {};
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0b1610]">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0b1610] via-[#123021] to-[#0b1610]" />
      {video && (
        <>
          <video data-testid="menu-video" src={video} autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#0b1610]/60" />
        </>
      )}

      <div className="relative mx-auto max-w-3xl px-5 py-8">
        <div className="flex items-center justify-between">
          {user ? (
            <Link to="/perfil" data-testid="menu-profile-link" className="flex items-center gap-2 rounded-full bg-white/10 py-1 pl-1 pr-3 backdrop-blur transition hover:bg-white/20">
              {userPhotoUrl(user) ? <img src={userPhotoUrl(user)} alt="" className="h-8 w-8 rounded-full object-cover" /> : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20"><User className="h-4 w-4 text-white" /></span>}
              <span className="max-w-[120px] truncate text-sm font-semibold text-white">{user?.name || "Perfil"}</span>
            </Link>
          ) : <span />}
          <div className="flex items-center gap-2">
            {isStaff ? (
              <button data-testid="menu-admin-link" onClick={() => navigate("/mind")} className="inline-flex items-center gap-1.5 rounded-full bg-[#E8611A] px-3 py-1.5 text-xs font-bold text-white transition hover:bg-[#CF5212]"><ShieldCheck className="h-4 w-4" /> Administração</button>
            ) : null}
            {user ? (
              <button data-testid="menu-logout-btn" onClick={logout} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur transition hover:bg-white/20"><LogOut className="h-4 w-4" /> Sair</button>
            ) : (
              <button data-testid="menu-login-btn" onClick={() => navigate("/login")} className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-800 transition hover:bg-slate-100"><LogIn className="h-4 w-4" /> Entrar</button>
            )}
          </div>
        </div>

        <div className="mt-6 text-center">
          <img src={logo} alt="Dubal Esportes" className="animate-float mx-auto w-56 sm:w-64" />
        </div>

        <BannerCarousel placement="menu" className="mt-6" />

        {pending && (
          <div data-testid="pending-banner" className="mt-6 flex items-center gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-amber-100">
            <Clock className="h-5 w-5 shrink-0" />
            <p className="text-sm">Sua conta está <strong>aguardando aprovação</strong> do administrador. Toque em <strong>{t("help_title")}</strong> para solicitar acesso. Assim que aprovada, os demais botões serão liberados.</p>
          </div>
        )}

        {!pending && order && (
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {visibleKeys.map((k) => OPTIONS.find((o) => o.key === k)).map((o, i) => <MenuCard key={o.key} o={o} i={i} bg={bgStyle(o.key)} hasBg={!!mbg[o.key]?.present} t={t} />)}
          </div>
        )}

        <a href={HELP_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="menu-help" className="mt-4 flex flex-wrap items-center justify-center gap-2 rounded-3xl border border-white/15 bg-white/5 px-4 py-4 text-white backdrop-blur transition hover:bg-white/10">
          <LifeBuoy className="h-5 w-5 text-emerald-300" />
          <span className="font-semibold">{t("help_title")}</span>
          <span className="text-sm text-emerald-100/70">{t("help_sub")}</span>
        </a>

        <InstallAppButton />
      </div>
    </div>
  );
}
